import { useState } from 'react'
import { Head, Link, router, usePage } from '@inertiajs/react'
import AppLayout from '../../layouts/AppLayout'
import { Building2, User, Mail, Shield, Database, CheckCircle, XCircle, Settings, Zap, ChevronRight, Video, Copy, Check, AlertTriangle, Loader2, Trash2, HardDriveDownload, Download } from 'lucide-react'
import { PageProps } from '@/types'

interface Organization {
  id: number
  name: string
  slug: string
  settings: Record<string, any>
  created_at: string
}

interface UserInfo {
  id: number
  email: string
  first_name: string
  last_name: string
  role: string
  created_at: string
}

interface DocusealWebhook {
  production_url: string | null
  production_source: string | null
  production_warning: string | null
  current_host_url: string
  current_host_matches_production: boolean
  signature_verification_enforced: boolean
  signature_secret_env_var: string
  event_types: string[]
  last_received_at: string | null
  last_event_type: string | null
  last_rejected_at: string | null
}

interface Integrations {
  sendgrid_configured: boolean
  sendgrid_ready?: boolean
  sendgrid_blocking_reason?: string | null
  docuseal_configured: boolean
  docuseal_webhook: DocusealWebhook
}

interface FeatureFlags {
  training_center_visible: boolean
  hide_commissions_and_equity: boolean
}

interface ResetCategory {
  key: string
  label: string
  count: number
}

interface DataResetPreview {
  delete: ResetCategory[]
  keep: ResetCategory[]
  total_delete: number
}

interface SnapshotEntry {
  filename: string
  size_bytes: number
  created_at: string
}

interface SnapshotStatus {
  ready: boolean
  reason: string | null
  message: string | null
  directory: string | null
  retention_count: number
  timeout_seconds: number
  free_space_bytes: number | null
  client_version: number | null
  server_version: number | null
  snapshots: SnapshotEntry[]
}

interface Props {
  organization: Organization
  user: UserInfo
  // Integration configuration is admin/owner-only; everyone else gets null.
  integrations: Integrations | null
  feature_flags: FeatureFlags
  data_reset: DataResetPreview | null
  snapshots: SnapshotStatus | null
}

export default function SettingsIndex({ organization, user, integrations, feature_flags, data_reset, snapshots }: Props) {
  const { auth } = usePage<PageProps>().props
  const canManageFlags = !!auth.user && ['administrator', 'owner'].includes(auth.user.role)
  const isOwner = auth.user?.role === 'owner'
  const [copiedUrlKey, setCopiedUrlKey] = useState<string | null>(null)
  const [resetPreview, setResetPreview] = useState<DataResetPreview | null>(data_reset)
  const [resetArmed, setResetArmed] = useState(false)
  const [resetConfirmText, setResetConfirmText] = useState('')
  const [isResetting, setIsResetting] = useState(false)
  const [isSnapshotting, setIsSnapshotting] = useState(false)

  const createSnapshot = () => {
    setIsSnapshotting(true)
    router.post('/settings/snapshots', {}, {
      preserveScroll: true,
      onFinish: () => setIsSnapshotting(false)
    })
  }

  // A version we could not read is shown as "unknown" rather than as a blank —
  // "the client is  and the server is 16" reads like a rendering bug.
  const formatMajorVersion = (major: number | null) => (major === null ? 'unknown' : String(major))

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    const units = ['KB', 'MB', 'GB', 'TB']
    let value = bytes / 1024
    let unit = 0
    while (value >= 1024 && unit < units.length - 1) {
      value /= 1024
      unit += 1
    }
    return `${value.toFixed(1)} ${units[unit]}`
  }

  const armReset = async () => {
    setResetArmed(true)
    // Counts may be minutes old by the time an owner reaches this section.
    try {
      const response = await fetch('/settings/data_reset/preview', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      })
      if (response.ok) setResetPreview(await response.json())
    } catch {
      // Keep the counts rendered from the page props.
    }
  }

  const runDataReset = () => {
    setIsResetting(true)
    router.post('/settings/data_reset', { confirmation: resetConfirmText }, {
      onFinish: () => {
        setIsResetting(false)
        setResetArmed(false)
        setResetConfirmText('')
      }
    })
  }

  const resetConfirmValid = resetConfirmText.trim() === organization.name.trim()
  const formatCount = (value: number) => value.toLocaleString()

  const copyUrl = async (key: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopiedUrlKey(key)
      setTimeout(() => setCopiedUrlKey((current) => (current === key ? null : current)), 2000)
    } catch {
      // Clipboard API unavailable; ignore
    }
  }

  const formatTimestamp = (value: string) => {
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString()
  }

  const hostOf = (url: string) => {
    try {
      return new URL(url).host
    } catch {
      return url
    }
  }

  const toggleTrainingCenter = () => {
    router.patch('/settings/feature_flags', {
      training_center_visible: !feature_flags.training_center_visible
    }, { preserveScroll: true })
  }

  const toggleHideCommissionsAndEquity = () => {
    router.patch('/settings/feature_flags', {
      hide_commissions_and_equity: !feature_flags.hide_commissions_and_equity
    }, { preserveScroll: true })
  }

  return (
    <AppLayout>
      <Head title="Settings" />
      
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-display font-bold text-chestnut-charcoal">Settings</h1>
          <p className="text-chestnut-charcoal/60 mt-1">Manage your organization and account settings</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-chestnut-charcoal/5">
            <div className="p-6 border-b border-chestnut-charcoal/10">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-chestnut-forest/10 rounded-lg">
                  <Building2 className="h-5 w-5 text-chestnut-forest" />
                </div>
                <h2 className="text-lg font-semibold text-chestnut-charcoal">Organization</h2>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal/60 mb-1">Organization Name</label>
                <p className="text-chestnut-charcoal font-medium">{organization.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal/60 mb-1">Slug</label>
                <p className="text-chestnut-charcoal">{organization.slug}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal/60 mb-1">Created</label>
                <p className="text-chestnut-charcoal">{new Date(organization.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-chestnut-charcoal/5">
            <div className="p-6 border-b border-chestnut-charcoal/10">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-chestnut-gold/20 rounded-lg">
                  <User className="h-5 w-5 text-chestnut-gold" />
                </div>
                <h2 className="text-lg font-semibold text-chestnut-charcoal">Your Account</h2>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal/60 mb-1">Name</label>
                <p className="text-chestnut-charcoal font-medium">{user.first_name} {user.last_name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal/60 mb-1">Email</label>
                <p className="text-chestnut-charcoal">{user.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal/60 mb-1">Role</label>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-chestnut-forest/10 text-chestnut-forest capitalize">
                  <Shield className="h-4 w-4" />
                  {user.role.replace('_', ' ')}
                </span>
              </div>
              {/* This card is read-only, and nothing here used to say where the
                  address could actually be changed. */}
              <Link
                href="/profile"
                className="inline-flex items-center gap-1.5 pt-2 text-sm font-medium text-chestnut-forest hover:underline"
              >
                Change your email or password
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>

        {canManageFlags && (
          <div className="bg-white rounded-xl shadow-sm border border-chestnut-charcoal/5">
            <div className="p-6 border-b border-chestnut-charcoal/10">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Video className="h-5 w-5 text-amber-600" />
                </div>
                <h2 className="text-lg font-semibold text-chestnut-charcoal">Feature Visibility</h2>
              </div>
            </div>
            <div className="p-6 divide-y divide-chestnut-charcoal/10">
              <div className="flex items-center justify-between pb-6">
                <div className="pr-4">
                  <p className="font-medium text-chestnut-charcoal">Show Training Center to Agents</p>
                  <p className="text-sm text-chestnut-charcoal/60 mt-1">
                    When off, agents won't see the Training section in their navigation and direct visits return a 404. Admins can always access training to prepare content.
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={feature_flags.training_center_visible}
                  onClick={toggleTrainingCenter}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-chestnut-forest focus:ring-offset-2 ${
                    feature_flags.training_center_visible ? 'bg-chestnut-forest' : 'bg-chestnut-charcoal/20'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      feature_flags.training_center_visible ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between pt-6">
                <div className="pr-4">
                  <p className="font-medium text-chestnut-charcoal">Hide Commissions and Equity</p>
                  <p className="text-sm text-chestnut-charcoal/60 mt-1">
                    When on, staff lose the Commissions, Pending Commissions, Payments and Equity pages, and agents lose My Commissions and My Equity. Dollar amounts also disappear from their dashboard, My Team and the agent tree. Owners and administrators are unaffected.
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={feature_flags.hide_commissions_and_equity}
                  onClick={toggleHideCommissionsAndEquity}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-chestnut-forest focus:ring-offset-2 ${
                    feature_flags.hide_commissions_and_equity ? 'bg-chestnut-forest' : 'bg-chestnut-charcoal/20'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      feature_flags.hide_commissions_and_equity ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        )}

        {integrations && (
        <div className="bg-white rounded-xl shadow-sm border border-chestnut-charcoal/5">
          <div className="p-6 border-b border-chestnut-charcoal/10">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Database className="h-5 w-5 text-blue-600" />
              </div>
              <h2 className="text-lg font-semibold text-chestnut-charcoal">Integrations</h2>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="p-4 border border-chestnut-charcoal/10 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-chestnut-charcoal/60" />
                    <span className="font-medium text-chestnut-charcoal">SendGrid</span>
                  </div>
                  {integrations.sendgrid_configured ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <XCircle className="h-5 w-5 text-chestnut-charcoal/30" />
                  )}
                </div>
                <p className="text-sm text-chestnut-charcoal/60">
                  {integrations.sendgrid_configured
                    ? 'Email sending configured'
                    : integrations.sendgrid_blocking_reason || 'Not configured'}
                </p>
              </div>

              <div className="p-4 border border-chestnut-charcoal/10 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-chestnut-charcoal/60" />
                    <span className="font-medium text-chestnut-charcoal">DocuSeal</span>
                  </div>
                  {integrations.docuseal_configured ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <XCircle className="h-5 w-5 text-chestnut-charcoal/30" />
                  )}
                </div>
                <p className="text-sm text-chestnut-charcoal/60">
                  {integrations.docuseal_configured ? 'E-signatures configured' : 'Not configured'}
                </p>
              </div>
            </div>

            <div className="p-4 border border-chestnut-charcoal/10 rounded-lg mb-4 space-y-5">
              <div>
                <h3 className="font-medium text-chestnut-charcoal">DocuSeal webhook</h3>
                <p className="text-sm text-chestnut-charcoal/60 mt-1">
                  DocuSeal calls this app when an agent views or signs a contract. Without a working
                  webhook, signed contracts are never marked as signed.
                </p>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-chestnut-charcoal/70 mb-1">
                    Production URL — paste this into DocuSeal
                  </label>
                  {integrations.docuseal_webhook.production_url ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        readOnly
                        aria-label="Production webhook URL"
                        value={integrations.docuseal_webhook.production_url}
                        onFocus={(e) => e.target.select()}
                        className="flex-1 min-w-0 px-3 py-2 text-sm text-chestnut-charcoal bg-chestnut-charcoal/5 border border-chestnut-charcoal/10 rounded-lg font-mono"
                      />
                      <button
                        type="button"
                        onClick={() => copyUrl('production', integrations.docuseal_webhook.production_url as string)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-chestnut-forest text-white rounded-lg hover:bg-chestnut-forest/90 transition-colors whitespace-nowrap"
                      >
                        {copiedUrlKey === 'production' ? (
                          <>
                            <Check className="h-4 w-4" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4" />
                            Copy production URL
                          </>
                        )}
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2 p-3 rounded-lg border border-amber-200 bg-amber-50 text-sm text-amber-900">
                      <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span>{integrations.docuseal_webhook.production_warning}</span>
                    </div>
                  )}
                </div>

                {!integrations.docuseal_webhook.current_host_matches_production && (
                  <div>
                    <label className="block text-xs font-medium text-chestnut-charcoal/70 mb-1">
                      This environment ({hostOf(integrations.docuseal_webhook.current_host_url)}) — only for
                      testing from here
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        readOnly
                        aria-label="Current environment webhook URL"
                        value={integrations.docuseal_webhook.current_host_url}
                        onFocus={(e) => e.target.select()}
                        className="flex-1 min-w-0 px-3 py-2 text-sm text-chestnut-charcoal bg-chestnut-charcoal/5 border border-chestnut-charcoal/10 rounded-lg font-mono"
                      />
                      <button
                        type="button"
                        onClick={() => copyUrl('current', integrations.docuseal_webhook.current_host_url)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-chestnut-charcoal/20 text-chestnut-charcoal rounded-lg hover:bg-chestnut-charcoal/5 transition-colors whitespace-nowrap"
                      >
                        {copiedUrlKey === 'current' ? (
                          <>
                            <Check className="h-4 w-4" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4" />
                            Copy this URL
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-chestnut-charcoal/70 mb-1">Signature verification</p>
                  {integrations.docuseal_webhook.signature_verification_enforced ? (
                    <p className="text-sm text-chestnut-charcoal">
                      <span className="inline-flex items-center gap-1.5 font-medium text-green-700">
                        <CheckCircle className="h-4 w-4" />
                        Enforced
                      </span>
                      {' — '}deliveries must be signed with{' '}
                      <code className="font-mono">{integrations.docuseal_webhook.signature_secret_env_var}</code>,
                      and the same value has to be set in DocuSeal.
                    </p>
                  ) : (
                    <p className="text-sm text-chestnut-charcoal">
                      <span className="inline-flex items-center gap-1.5 font-medium text-amber-700">
                        <AlertTriangle className="h-4 w-4" />
                        Skipped
                      </span>
                      {' — '}any caller reaching this URL is accepted. Set{' '}
                      <code className="font-mono">{integrations.docuseal_webhook.signature_secret_env_var}</code>{' '}
                      here and in DocuSeal to enforce signatures.
                    </p>
                  )}
                </div>

                <div>
                  <p className="text-xs font-medium text-chestnut-charcoal/70 mb-1">Events to enable in DocuSeal</p>
                  <div className="flex flex-wrap gap-1.5">
                    {integrations.docuseal_webhook.event_types.map((event) => (
                      <code
                        key={event}
                        className="px-2 py-1 text-xs font-mono bg-chestnut-charcoal/5 border border-chestnut-charcoal/10 rounded"
                      >
                        {event}
                      </code>
                    ))}
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-chestnut-charcoal/10">
                <p className="text-xs font-medium text-chestnut-charcoal/70 mb-1">Delivery health</p>
                {integrations.docuseal_webhook.last_received_at ? (
                  <p className="text-sm text-chestnut-charcoal flex items-center gap-1.5">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Last webhook accepted {formatTimestamp(integrations.docuseal_webhook.last_received_at)}
                    {integrations.docuseal_webhook.last_event_type
                      ? ` (${integrations.docuseal_webhook.last_event_type})`
                      : ''}
                  </p>
                ) : (
                  <p className="text-sm text-chestnut-charcoal/70 flex items-center gap-1.5">
                    <XCircle className="h-4 w-4 text-chestnut-charcoal/30" />
                    No webhook received yet — sign a test document, then reload this page.
                  </p>
                )}
                {integrations.docuseal_webhook.last_rejected_at && (
                  <p className="mt-1 text-sm text-red-700 flex items-center gap-1.5">
                    <AlertTriangle className="h-4 w-4" />
                    Last delivery rejected for a bad signature{' '}
                    {formatTimestamp(integrations.docuseal_webhook.last_rejected_at)}
                  </p>
                )}
              </div>
            </div>

            <Link 
              href="/settings/integrations" 
              className="inline-flex items-center gap-2 px-4 py-2 bg-chestnut-forest text-white rounded-lg hover:bg-chestnut-forest/90 transition-colors"
            >
              <Settings className="h-4 w-4" />
              Manage Integrations
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-chestnut-charcoal/5">
          <div className="p-6 border-b border-chestnut-charcoal/10">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Zap className="h-5 w-5 text-purple-600" />
              </div>
              <h2 className="text-lg font-semibold text-chestnut-charcoal">Workflow & Automations</h2>
            </div>
          </div>
          <div className="p-6">
            <p className="text-chestnut-charcoal/70 mb-4">
              Configure agent onboarding workflows, email templates, and automated notifications.
            </p>
            <Link 
              href="/settings/workflows" 
              className="inline-flex items-center gap-2 px-4 py-2 bg-chestnut-forest text-white rounded-lg hover:bg-chestnut-forest/90 transition-colors"
            >
              <Settings className="h-4 w-4" />
              Manage Workflows
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        {isOwner && resetPreview && (
          <div className="rounded-xl border-2 border-red-300 bg-red-50/60 shadow-sm">
            <div className="p-6 border-b border-red-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-red-800">Danger Zone</h2>
                  <p className="text-sm text-red-700/80">Owner only. These actions are permanent.</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-5">
              <div>
                <h3 className="font-semibold text-red-800">Reset production data</h3>
                <p className="text-sm text-red-900/80 mt-1">
                  Deletes all operational data so you can restart with real agents and real commissions.
                  ID numbering restarts, so the first new agent and commission get low IDs.
                  There is no undo inside the app — recovery means restoring the snapshot taken just
                  before the reset from a Railway shell.
                </p>
              </div>

              {snapshots && (
                <div className="rounded-lg border border-red-200 bg-white p-4 space-y-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-semibold text-chestnut-charcoal flex items-center gap-2">
                        <HardDriveDownload className="h-4 w-4" />
                        Database snapshots
                      </p>
                      <p className="text-sm text-chestnut-charcoal/70 mt-1">
                        A full snapshot is taken automatically right before a reset. If it cannot be
                        written, the reset is cancelled and nothing is deleted.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={createSnapshot}
                      disabled={!snapshots.ready || isSnapshotting}
                      className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-chestnut-forest text-white font-medium rounded-lg hover:bg-chestnut-forest/90 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {isSnapshotting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Creating snapshot...
                        </>
                      ) : (
                        <>
                          <HardDriveDownload className="h-4 w-4" />
                          Create snapshot now
                        </>
                      )}
                    </button>
                  </div>

                  {snapshots.ready ? (
                    <p className="text-sm text-green-800 flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span>
                        Snapshots are working. Written to <code className="font-mono text-xs">{snapshots.directory}</code>;
                        the {snapshots.retention_count} most recent are kept.
                      </span>
                    </p>
                  ) : (
                    <p className="text-sm text-red-800 flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span>
                        <span className="font-semibold">Snapshots are unavailable, so a reset cannot run.</span>{' '}
                        {snapshots.message}
                      </span>
                    </p>
                  )}

                  {/* Shown in both states: an owner can see which client the image
                      shipped and which server it is talking to without a shell. */}
                  <p className="text-xs text-chestnut-charcoal/60">
                    {`pg_dump client: major ${formatMajorVersion(snapshots.client_version)} · database server: major ${formatMajorVersion(snapshots.server_version)}`}
                  </p>

                  {snapshots.snapshots.length > 0 ? (
                    <ul className="divide-y divide-chestnut-charcoal/10 border border-chestnut-charcoal/10 rounded-lg">
                      {snapshots.snapshots.map((snapshot) => (
                        <li key={snapshot.filename} className="flex items-center justify-between gap-3 px-3 py-2">
                          <div className="min-w-0">
                            <p className="text-sm font-mono text-chestnut-charcoal truncate">{snapshot.filename}</p>
                            <p className="text-xs text-chestnut-charcoal/60">
                              {new Date(snapshot.created_at).toLocaleString()} · {formatBytes(snapshot.size_bytes)}
                            </p>
                          </div>
                          <a
                            href={`/settings/snapshots/${snapshot.filename}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-chestnut-charcoal/20 rounded-lg hover:bg-chestnut-charcoal/5 whitespace-nowrap"
                          >
                            <Download className="h-4 w-4" />
                            Download
                          </a>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-chestnut-charcoal/60">No snapshots yet.</p>
                  )}

                  <p className="text-xs text-chestnut-charcoal/60">
                    Snapshot files contain credentials and every agent's personal data — store downloads securely.
                    Restoring one is an operator command run from a Railway shell (see the technical reference).
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-lg border border-red-200 bg-white p-4">
                  <p className="text-sm font-semibold text-red-800 mb-3">
                    Will be deleted ({formatCount(resetPreview.total_delete)} records)
                  </p>
                  <ul className="space-y-1">
                    {resetPreview.delete.map((row) => (
                      <li key={row.key} className="flex items-center justify-between text-sm">
                        <span className="text-chestnut-charcoal/70">{row.label}</span>
                        <span className={`font-mono ${row.count > 0 ? 'text-red-700 font-semibold' : 'text-chestnut-charcoal/40'}`}>
                          {formatCount(row.count)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="rounded-lg border border-green-200 bg-white p-4">
                  <p className="text-sm font-semibold text-green-800 mb-3">Will be kept</p>
                  <ul className="space-y-1">
                    {resetPreview.keep.map((row) => (
                      <li key={row.key} className="flex items-center justify-between text-sm">
                        <span className="text-chestnut-charcoal/70">{row.label}</span>
                        <span className="font-mono text-green-700 font-semibold">{formatCount(row.count)}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-chestnut-charcoal/60 mt-3">
                    You stay signed in. Demo <code className="font-mono">@chestnut.test</code> accounts are removed.
                  </p>
                </div>
              </div>

              {!resetArmed ? (
                <button
                  type="button"
                  onClick={armReset}
                  disabled={!!snapshots && !snapshots.ready}
                  className="inline-flex items-center gap-2 px-4 py-2 border-2 border-red-600 text-red-700 font-medium rounded-lg hover:bg-red-600 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                  Reset production data
                </button>
              ) : (
                <div className="rounded-lg border border-red-300 bg-white p-4 space-y-4">
                  <p className="text-sm text-chestnut-charcoal/80">
                    A full database snapshot will be written and verified first. If it cannot be
                    created, the reset is cancelled and nothing is deleted.
                  </p>
                  <div>
                    <label htmlFor="reset-confirmation" className="block text-sm font-medium text-chestnut-charcoal mb-2">
                      Type <span className="font-mono text-red-700">{organization.name}</span> to confirm
                    </label>
                    <input
                      id="reset-confirmation"
                      type="text"
                      autoComplete="off"
                      value={resetConfirmText}
                      onChange={(e) => setResetConfirmText(e.target.value)}
                      placeholder={organization.name}
                      disabled={isResetting}
                      className="w-full px-4 py-2 border border-chestnut-charcoal/20 rounded-lg focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={runDataReset}
                      disabled={!resetConfirmValid || isResetting}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isResetting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Resetting...
                        </>
                      ) : (
                        <>
                          <Trash2 className="h-4 w-4" />
                          Permanently delete all operational data
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setResetArmed(false); setResetConfirmText('') }}
                      disabled={isResetting}
                      className="px-4 py-2 text-chestnut-charcoal/70 hover:text-chestnut-charcoal font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-chestnut-charcoal/5">
          <div className="p-6 border-b border-chestnut-charcoal/10">
            <h2 className="text-lg font-semibold text-chestnut-charcoal">Configuration Notes</h2>
          </div>
          <div className="p-6">
            <div className="prose prose-sm text-chestnut-charcoal/80">
              <p>
                To enable integrations, configure the following environment variables:
              </p>
              <ul className="mt-2 space-y-1">
                <li><code className="bg-chestnut-cream px-1.5 py-0.5 rounded text-xs">SENDGRID_API_KEY</code> - For email notifications</li>
                <li><code className="bg-chestnut-cream px-1.5 py-0.5 rounded text-xs">DOCUSEAL_API_KEY</code> - For electronic signatures</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
